"use client";

import React, {useMemo} from "react";
import { Stack, Typography } from "@mui/material";
import {useLocale, useTranslations} from "next-intl";
import { ColletteDivider, ColletteIconButton } from "@collette-travel/collette-common-controls";
import { Close } from "@mui/icons-material";
import RequestAQuoteSubmissionMessage from "@/components/forms/request-a-quote/request-a-quote-submission-message";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import { useForm } from "react-hook-form";
import RequestAQuoteForm from "@/components/forms/request-a-quote/request-a-quote-form";
import DataLayer from "@/components/data-layer/data-layer";
import raqDataLayerPushFactory from "@/factories/data-layer/raq-data-layer-push-factory";
import useLeadSubmit from "@/hooks/forms/on-submit/use-lead-submit";
import {RAQFormData} from "@/types/leads/lead-forms";

interface RequestAQuoteProps {
  handleClose: () => void;
  tourId?: string;
  landingPageId?: string;
  defaultDate?: string;
  tourTitle?: string;
}

const RequestAQuoteSection = ({
  handleClose,
  tourId,
  landingPageId,
  defaultDate,
  tourTitle
}: RequestAQuoteProps) => {
  const t = useTranslations("FORMS.RAQ");
  const sourceUrl = window.location.href;
  const locale = useLocale();

  const {
    handleSubmit,
    formState: { isSubmitSuccessful, isSubmitted, isSubmitting, isValid },
    control,
    getValues
  } = useForm<RAQFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isTravelAgent: "",
      optInEmail: locale === "en-us",
      workingWithAgent: false,
      withMoreTravelers: false,
      tourDepartureDate: defaultDate ?? "",
      tourId: landingPageId ?? "",
      recaptchaToken: undefined,
      tourTitle: tourTitle ?? ""
    }
  });

  const { onSubmit, serverError } = useLeadSubmit<RAQFormData>({
    locale,
    sourceUrl,
    leadType: "RAQ"
  });
  
  const dataLayerData = useMemo(() => 
    raqDataLayerPushFactory(getValues(), sourceUrl),
    [getValues, sourceUrl, isSubmitSuccessful]); // needs to update when isSubmitted changes

  return (
    <Stack
      sx={{
        borderRadius: "1rem",
        backgroundColor: "background.default"
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        gap="1rem"
        alignItems="flex-start"
        padding={{ xs: "2rem 2.4rem", md: "4rem 4rem 0" }}
      >
        <Stack flexGrow={1}>
          <Typography variant="h3" mb="1.2rem" data-testid={`raq-header_${tourId}`}>
            {t("Request a Quote")}
          </Typography>
          {!isSubmitSuccessful && (
            <Typography mb="2.4rem" data-testid={`raq-copy_${tourId}`}>
              {t("copy")}
            </Typography>
          )}
          <ColletteDivider sx={{ borderColor: "divider" }} />
        </Stack>
        <ColletteIconButton
          testId="raq-close-button"
          onClick={handleClose}
          sx={{
            mt: "-0.8rem",
            mr: "-0.8rem"
          }}
        >
          <Close />
        </ColletteIconButton>
      </Stack>
      {/* Form and submission response */}
      {isSubmitSuccessful && (
        <RequestAQuoteSubmissionMessage handleClose={handleClose} error={serverError} />
      )}
      {!isSubmitSuccessful && (
        <RequestAQuoteForm
          isInvalid={!isValid && isSubmitted}
          control={control}
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
        />
      )}
      {/* Privacy Policy */}
      {!isSubmitSuccessful && <HubspotFormDisclaimer testId={`raq-disclaimer_${tourId}`} />}
      {isSubmitSuccessful && <DataLayer data={dataLayerData} />}
    </Stack>
  );
};

export default RequestAQuoteSection;
